import type {
  ServiceExpressionValue,
  ServiceExpressionVariables,
} from '@louez/types'

type TokenType =
  | 'number'
  | 'string'
  | 'identifier'
  | 'operator'
  | 'leftParen'
  | 'rightParen'
  | 'comma'
  | 'end'

interface Token {
  type: TokenType
  value: string
}

const twoCharacterOperators = new Set(['>=', '<=', '==', '!=', '&&', '||'])
const oneCharacterOperators = new Set(['+', '-', '*', '/', '>', '<'])

function tokenize(expression: string): Token[] {
  const tokens: Token[] = []
  let position = 0

  while (position < expression.length) {
    const character = expression[position]

    if (/\s/.test(character)) {
      position += 1
      continue
    }

    const pair = expression.slice(position, position + 2)
    if (twoCharacterOperators.has(pair)) {
      tokens.push({ type: 'operator', value: pair })
      position += 2
      continue
    }

    if (oneCharacterOperators.has(character)) {
      tokens.push({ type: 'operator', value: character })
      position += 1
      continue
    }

    if (character === '(') {
      tokens.push({ type: 'leftParen', value: character })
      position += 1
      continue
    }

    if (character === ')') {
      tokens.push({ type: 'rightParen', value: character })
      position += 1
      continue
    }

    if (character === ',') {
      tokens.push({ type: 'comma', value: character })
      position += 1
      continue
    }

    if (character === '"') {
      const start = position
      let value = ''
      let closed = false
      position += 1

      while (position < expression.length) {
        const current = expression[position]
        if (current === '"') {
          closed = true
          position += 1
          break
        }
        if (current === '\\') {
          const escaped = expression[position + 1]
          if (escaped !== '"' && escaped !== '\\') {
            throw new Error(`Unsupported escape sequence at position ${position}`)
          }
          value += escaped
          position += 2
          continue
        }
        if (current.charCodeAt(0) < 32) {
          throw new Error(`Control character in string at position ${position}`)
        }
        value += current
        position += 1
      }

      if (!closed) {
        throw new Error(`Unterminated string at position ${start}`)
      }
      tokens.push({ type: 'string', value })
      continue
    }

    if (/[0-9.]/.test(character)) {
      const start = position
      let decimalPoints = 0
      while (position < expression.length && /[0-9.]/.test(expression[position])) {
        if (expression[position] === '.') decimalPoints += 1
        position += 1
      }
      const value = expression.slice(start, position)
      if (decimalPoints > 1 || value === '.') {
        throw new Error(`Invalid number at position ${start}`)
      }
      tokens.push({ type: 'number', value })
      continue
    }

    if (/[a-zA-Z_]/.test(character)) {
      const start = position
      while (
        position < expression.length &&
        /[a-zA-Z0-9_]/.test(expression[position])
      ) {
        position += 1
      }
      tokens.push({
        type: 'identifier',
        value: expression.slice(start, position),
      })
      continue
    }

    throw new Error(`Unexpected character "${character}" at position ${position}`)
  }

  tokens.push({ type: 'end', value: '' })
  return tokens
}

function requireNumber(value: ServiceExpressionValue, context: string): number {
  if (typeof value !== 'number') {
    throw new Error(`${context} requires a number`)
  }
  return value
}

function requireBoolean(value: ServiceExpressionValue, context: string): boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${context} requires a boolean`)
  }
  return value
}

function ensureFinite(value: number): number {
  if (!Number.isFinite(value)) {
    throw new Error('Expression result is not finite')
  }
  return value
}

class ExpressionParser {
  private position = 0

  constructor(
    private readonly tokens: Token[],
    private readonly variables: ServiceExpressionVariables,
  ) {}

  parse(): ServiceExpressionValue {
    const result = this.parseOr()
    if (this.current().type !== 'end') {
      throw new Error(`Unexpected token "${this.current().value}"`)
    }
    return result
  }

  private current(): Token {
    return this.tokens[this.position]
  }

  private consume(): Token {
    const token = this.current()
    this.position += 1
    return token
  }

  private matchOperator(operator: string): boolean {
    const token = this.current()
    if (token.type === 'operator' && token.value === operator) {
      this.consume()
      return true
    }
    return false
  }

  private parseOr(): ServiceExpressionValue {
    let value = this.parseAnd()
    while (this.matchOperator('||')) {
      const left = requireBoolean(value, 'Operator ||')
      const right = requireBoolean(this.parseAnd(), 'Operator ||')
      value = left || right
    }
    return value
  }

  private parseAnd(): ServiceExpressionValue {
    let value = this.parseComparison()
    while (this.matchOperator('&&')) {
      const left = requireBoolean(value, 'Operator &&')
      const right = requireBoolean(this.parseComparison(), 'Operator &&')
      value = left && right
    }
    return value
  }

  private parseComparison(): ServiceExpressionValue {
    const left = this.parseAdditive()
    const token = this.current()
    if (
      token.type !== 'operator' ||
      !['>', '>=', '<', '<=', '==', '!='].includes(token.value)
    ) {
      return left
    }

    this.consume()
    const right = this.parseAdditive()

    if (token.value === '==' || token.value === '!=') {
      if (typeof left !== typeof right) {
        throw new Error(
          `Operator ${token.value} requires operands of the same type`,
        )
      }
      return token.value === '==' ? left === right : left !== right
    }

    const leftNumber = requireNumber(left, `Operator ${token.value}`)
    const rightNumber = requireNumber(right, `Operator ${token.value}`)

    if (token.value === '>') return leftNumber > rightNumber
    if (token.value === '>=') return leftNumber >= rightNumber
    if (token.value === '<') return leftNumber < rightNumber
    return leftNumber <= rightNumber
  }

  private parseAdditive(): ServiceExpressionValue {
    let value = this.parseMultiplicative()

    while (
      this.current().type === 'operator' &&
      ['+', '-'].includes(this.current().value)
    ) {
      const operator = this.consume().value
      const left = requireNumber(value, `Operator ${operator}`)
      const right = requireNumber(
        this.parseMultiplicative(),
        `Operator ${operator}`,
      )
      value = ensureFinite(operator === '+' ? left + right : left - right)
    }

    return value
  }

  private parseMultiplicative(): ServiceExpressionValue {
    let value = this.parseUnary()

    while (
      this.current().type === 'operator' &&
      ['*', '/'].includes(this.current().value)
    ) {
      const operator = this.consume().value
      const left = requireNumber(value, `Operator ${operator}`)
      const right = requireNumber(this.parseUnary(), `Operator ${operator}`)
      if (operator === '/' && right === 0) {
        throw new Error('Division by zero')
      }
      value = ensureFinite(operator === '*' ? left * right : left / right)
    }

    return value
  }

  private parseUnary(): ServiceExpressionValue {
    if (this.matchOperator('+')) {
      return requireNumber(this.parseUnary(), 'Unary +')
    }
    if (this.matchOperator('-')) {
      return ensureFinite(-requireNumber(this.parseUnary(), 'Unary -'))
    }
    return this.parsePrimary()
  }

  private parsePrimary(): ServiceExpressionValue {
    const token = this.consume()

    if (token.type === 'number') {
      return ensureFinite(Number(token.value))
    }

    if (token.type === 'string') {
      return token.value
    }

    if (token.type === 'identifier') {
      if (this.current().type === 'leftParen') {
        return this.parseFunction(token.value)
      }
      if (token.value === 'true') return true
      if (token.value === 'false') return false
      if (!Object.hasOwn(this.variables, token.value)) {
        throw new Error(`Unknown variable "${token.value}"`)
      }
      return this.variables[token.value]
    }

    if (token.type === 'leftParen') {
      const value = this.parseOr()
      if (this.consume().type !== 'rightParen') {
        throw new Error('Missing closing parenthesis')
      }
      return value
    }

    throw new Error(`Unexpected token "${token.value}"`)
  }

  private parseFunction(name: string): number {
    const functions = new Set(['min', 'max', 'ceil', 'floor', 'round'])
    if (!functions.has(name)) {
      throw new Error(`Unknown function "${name}"`)
    }

    this.consume()
    const values: number[] = []
    if (this.current().type !== 'rightParen') {
      do {
        values.push(requireNumber(this.parseOr(), `Function ${name}`))
      } while (this.current().type === 'comma' && this.consume())
    }

    if (this.consume().type !== 'rightParen') {
      throw new Error(`Missing closing parenthesis for function "${name}"`)
    }

    if ((name === 'min' || name === 'max') && values.length === 0) {
      throw new Error(`Function "${name}" requires at least one argument`)
    }
    if (
      (name === 'ceil' || name === 'floor' || name === 'round') &&
      values.length !== 1
    ) {
      throw new Error(`Function "${name}" requires exactly one argument`)
    }

    if (name === 'min') return ensureFinite(Math.min(...values))
    if (name === 'max') return ensureFinite(Math.max(...values))
    if (name === 'ceil') return ensureFinite(Math.ceil(values[0]))
    if (name === 'floor') return ensureFinite(Math.floor(values[0]))
    return ensureFinite(Math.round(values[0]))
  }
}

function parseExpression(
  expression: string,
  variables: ServiceExpressionVariables,
): ServiceExpressionValue {
  if (expression.length === 0 || expression.length > 512) {
    throw new Error('Expression length must be between 1 and 512 characters')
  }
  return new ExpressionParser(tokenize(expression), variables).parse()
}

export function evaluateFormula(
  expression: string,
  variables: ServiceExpressionVariables,
): number {
  return requireNumber(parseExpression(expression, variables), 'Formula')
}

export function evaluateCondition(
  expression: string,
  variables: ServiceExpressionVariables,
): boolean {
  return requireBoolean(parseExpression(expression, variables), 'Condition')
}
